import { useAuth, useBrokerContextSafe } from '@/app/context';
import { isBrokerUser, isInsurerUser } from '@/shared/utils/get-group';

export type NotificationsScope = {
  /** Há feed para este usuário agora? */
  enabled: boolean;
  /**
   * Corretora a consultar. Preenchida só para a seguradora (vem do seletor do
   * header); nos perfis de corretora fica vazia de propósito, porque o back
   * resolve o escopo pelo token e ignora o parâmetro — é o que impede um
   * corretor de ler o feed de outra corretora.
   */
  partyCode?: string;
  /**
   * A seguradora acompanha, mas não marca como lida: o aviso existe para o
   * corretor, e zerar o badge dele de fora o faria perder a emissão que estava
   * esperando.
   */
  canMarkAsRead: boolean;
};

/**
 * Quem vê notificações, e de qual corretora.
 *
 * Regra única do front, compartilhada pelo sino e pela tela:
 * - **corretora** (`broker.*`): vê o próprio feed, sempre;
 * - **seguradora** (`insurer.*`): vê o feed da corretora selecionada no header,
 *   em modo leitura. Sem corretora selecionada não há o que mostrar;
 * - demais perfis: sem feed nesta fase.
 *
 * Usa os `groups` SÍNCRONOS do `AuthContext` — o mesmo caminho do item de menu
 * (`sidebar-menu-item.component.tsx`). O hook `useIsBrokerUser` resolve por
 * `useEffect` assíncrono e devolve `null` até responder, o que esconderia o
 * sino na primeira renderização.
 */
export function useNotificationsScope(): NotificationsScope {
  const { groups } = useAuth();
  // `Safe` porque o sino vive no header, que também renderiza fora do provider
  // em cenários de erro/HMR.
  const brokerContext = useBrokerContextSafe();

  const userGroups = groups ?? [];

  if (isBrokerUser(userGroups)) {
    return { enabled: true, canMarkAsRead: true };
  }

  if (isInsurerUser(userGroups)) {
    const partyCode = brokerContext?.selectedBroker?.code;
    return {
      enabled: Boolean(partyCode),
      partyCode: partyCode || undefined,
      canMarkAsRead: false,
    };
  }

  return { enabled: false, canMarkAsRead: false };
}
